import React from "react";
import { connect } from 'react-redux';
import mapboxgl from "mapbox-gl";
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';

import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { toggleSwitch,
        selectedCardsGroupsChange, 
        popUpMenuChange, 
        visibleCardsGroupsChange,
        setBreakCoordinatesModal} from "../../actions/index.js";

import { sky, 
        // map3d,
        // terrainExaggeration,
        projectLevelLayer,
        projectLevelSource,
        createFeatureCollection,
        cardPointLayer, 
        cardLineStringLayer,
        cardPolygonLayer,
        cardOutlinePolygonLayer,
        getLevelData, 
        axleNamesLayer,
        axlesLineLayer } from "../../functions/map/mapboxDataSource.js";

import { getCardMarker } from './mapMarkers';
import mapManager from "../../functions/map/mapManager.js";
import { addHighlight, highlightManager,
        removeHighlight } from "../../functions/map/highlightManager.js";
import hoverManager from "../../functions/map/hoverManager.js";
import { getFeaturesCardsGroupIds,
        getHoveredFeatures } from "../../functions/cardsGroupsFeatures.js";
import { ToggleActions, allSelectableLayers } from "../../constants/constants.js";
import { getAxleFeatures } from "../../functions/map/axlesManager.js";


class Mapbox extends React.Component  {
    constructor(props) {
        super(props);
        this.state = {
          roundedArea: null
        };
        this.draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: {
                polygon: true,
                trash: true,
            },
            defaultMode: 'draw_polygon'
        });
    }
    

    componentDidMount() {
        this.initializeMap()
    }

    updateDrawControls(createBreakMode) {
        if (createBreakMode) {
            this.map.addControl(this.draw);
        } else {
            this.map.removeControl(this.draw);
        }
    }

    initializeMap() {
        const defaultZoom = 14;
        this.map = new mapboxgl.Map({
            container: this.mapContainer,
            style: this.props.mapStyle,
            zoom: defaultZoom,
            minZoom: defaultZoom,
            accessToken: process.env.REACT_APP_MAPBOX_TOKEN,
            maxPitch: 80,
            attributionControl: false,
            center: this.props.projectData.attributes.coordinates.center,
            boxZoom: false
        })
        const updateArea = (e) => {
            const data = this.draw.getAll();
            if (data.features.length > 0) {
                const area = turf.area(data);
                this.setState({ roundedArea: Math.round(area * 100) / 100 });
            } else {
                this.setState({ roundedArea: null });
                if (e.type !== 'draw.delete') {
                alert('Click the map to draw a polygon.');
                }
            }
        }
    
        this.map.on('draw.create', (e) => {
            //updateArea(e);
            const coordinates = e.features[0].geometry.coordinates[0];
            this.props.setBreakCoordinatesModal({coordinates:coordinates.slice(0, -1), show:true})
        });
        this.map.on('draw.delete', updateArea);
        this.map.on('draw.update', updateArea);

        let cardMarker = getCardMarker()
        let levelId = this.props.loadedLevels[0].toString()
        let levelData = getLevelData(this.props.projectData, this.props.projectData.levels[levelId])
        let [axleFeatures, axleNamesFeatures] = getAxleFeatures(this.props.projectData.axles, this.props.projectData.levels)

        this.map.on('load', () => {
            //this.map.setTerrain({ source: map3d, exaggeration: terrainExaggeration })
            this.map.addLayer(sky)
            this.map.addSource('projectLevel', projectLevelSource(levelData))
            this.map.addLayer(projectLevelLayer)
            this.map.addImage('cardMarker', cardMarker, { sdf: true })
            this.map.addSource('cards', createFeatureCollection([]))
            this.map.addSource('axles', createFeatureCollection(axleFeatures))
            this.map.addSource('axleNames', createFeatureCollection(axleNamesFeatures))
            this.map.addLayer(axlesLineLayer)
            this.map.addLayer(axleNamesLayer)
            this.map.addLayer(cardOutlinePolygonLayer)
            this.map.addLayer(cardPolygonLayer)
            this.map.addLayer(cardLineStringLayer)
            this.map.addLayer(cardPointLayer)
            this.props.toggleSwitch(ToggleActions.LOADED_MAP)
        })
        this.map.on('click', this.handleOpenPopup)

        hoverManager(this.map)
        highlightManager(this.map, this.props, this.handleOpenPopup)
    }

    handleOpenPopup = (e) => {
        removeHighlight(this.map)
        let bbox = [
            [e.point.x - 5, e.point.y - 5],
            [e.point.x + 5, e.point.y + 5]
        ];
        const selectedFeatures = this.map.queryRenderedFeatures(bbox, { layers: allSelectableLayers });
        const hoveredFeatures = getHoveredFeatures(selectedFeatures);
        if (hoveredFeatures.length) {
            let cardsGroupIds = getFeaturesCardsGroupIds(this.map, hoveredFeatures);
            if (cardsGroupIds.length === 0) return;
            addHighlight(this.map, hoveredFeatures);
            this.props.popUpMenuChange({ show: true });
            this.props.selectedCardsGroupsChange(cardsGroupIds);
        }
    }

    componentDidUpdate(prevProps) {
        mapManager(this.map, this.props, prevProps)
        if (prevProps.createBreakMode !== this.props.createBreakMode) {
            this.updateDrawControls(this.props.createBreakMode);
        }
    }

    render() {
        return(
            <div className='main-background' ref={el => this.mapContainer = el} />
        )
    } 
}

function mapStateToProps(state) {
    const mapStyle = process.env.REACT_APP_MAPBOX_STYLE;
    return {
        mapStyle       : mapStyle,
        projectData    : state.projectData,
        filters        : state.filters,
        timelineSelect : state.filters.timelineSelect,
        day            : state.day,
        selectedItem   : state.selectedItem,
        loadedMap      : state.switches.loadedMap,
        projectUsers   : state.projectUsers,
        popUpMenu      : state.popUpMenu,
        paint          : state.paint,
        opacity        : state.opacity,
        fullBuilding   : state.switches.fullBuilding,
        timeline       : state.switches.timeline,
        plannedTimeline: state.switches.plannedTimeline,
        axles          : state.switches.axles,
        roadLabel      : state.switches.roadLabel,
        loadingLevelId : state.loadingLevelId,
        loadingLevel   : state.switches.loadingLevel,
        refreshSwitch  : state.switches.refresh,
        loadedLevels   : state.loadedLevels,
        featuresGroups : state.featuresGroups,
        cardsGroupsWithHistoricalObservations : state.cardsGroupsWithHistoricalObservations,
        createBreakMode  : state.switches.createBreakMode,
    };
}

const actions = {
    toggleSwitch : toggleSwitch,
    selectedCardsGroupsChange : selectedCardsGroupsChange,
    popUpMenuChange : popUpMenuChange,
    visibleCardsGroupsChange : visibleCardsGroupsChange,
    setBreakCoordinatesModal : setBreakCoordinatesModal,
};

export default connect(mapStateToProps, actions)(Mapbox);